/* eslint-disable react/jsx-wrap-multilines */
import styled from 'styled-components';
import { useState } from 'react';
import PropTypes from 'prop-types';
import InputAdornment from '@material-ui/core/InputAdornment';
import UnderlineLink from '@comp/UnderlineLink/UnderlineLink';
import H2 from '@base/H2/H2';
import H6 from '@base/H6/H6';
import P1 from '@base/P1/P1';
import TextField from '@base/TextField/TextField';
import Button from '@base/Button/Button';
import useInterval from '@hooks/useInterval';
import { Spacings, Fonts, BgColors, TextColors } from '@styles/index';
import { getOtpError } from '@utils/formHelper';
import Constants from './constants';
import EnterOtherFields from './EnterOtherFields';
import CountryMenu from './CountryMenu';

const StyledP1 = styled(P1)`
  margin-top: ${Spacings.SPACING_3B};
  margin-bottom: ${Spacings.SPACING_4B};
`;

const StyledTextField = styled(TextField)`
  margin-top: ${Spacings.SPACING_4B};
  color: ${({ theme }) => theme.colors.fontGreyV2};
`;

const DisabledTextField = styled(TextField)`
  /* padding-left: 108px; */
  background: ${BgColors.DISABLED};
`;

const StyledH6 = styled(H6)`
  font-weight: ${Fonts.BOLD};
  min-width: ${({ fixedWidth }) => (fixedWidth ? '140px' : '28px')};
  ${({ theme }) => `${theme.breakpoints.down('mobile')} {
      min-width: 23px;
    };`}
`;

const Resend = styled(StyledH6)`
  ${({ theme }) => `${theme.breakpoints.down('mobile')} {
      min-width: auto;
    };`}
`;

const StyledH6WithMargins = styled(H6)`
  font-weight: ${Fonts.BOLD};
  margin: 0 ${Spacings.SPACING_2B};
  padding-bottom: ${Spacings.SPACING_2B};
`;

const StyledUnderlineLink = styled(UnderlineLink)`
  border-bottom-width: 0;
  padding-bottom: 0;
`;

const UnderlineLinkWrapper = styled.div`
  position: absolute;
  right: ${Spacings.SPACING_4B};
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${({ theme }) => `${theme.breakpoints.down('mobile')} {
      right: 0px;
    };`}
`;

const EditDiv = styled.div`
  position: relative;
`;

const Verify = styled(Button)`
  width: 150px;
`;

const Header = styled(H2)`
  ${({ theme }) => `${theme.breakpoints.down('mobile')} {
      font-size: 16px;
  };`}
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  margin-top: ${Spacings.SPACING_8B};
`;

const EnterOtpStep = ({
  accountType,
  mobileNumber,
  changeCountry,
  country,
  countries,
  otp,
  setOtp,
  resendOtp,
  editMobileNumber,
  fieldsData,
  setFieldsData,
  otpOrEmailResponse,
  submitLogin,
  loggingIn,
  states,
  error,
  sourceType,
  isTTU,
  ttuUserName
}) => {
  const [timeLeft, setTimeLeft] = useState(60);
  const startTimer = () => {
    setTimeLeft(60);
  };
  const onOtpChange = (e) => {
    const otpFilled = e.target.value.replace(/ /g, '');
    if (otpFilled.match(/^\d+$/) || otpFilled === '') {
      setOtp(otpFilled);
    }
  };
  const resend = (e) => {
    if (e) e.preventDefault();
    if (timeLeft === 0) {
      startTimer();
      resendOtp(1);
    }
  };
  const onCall = (e) => {
    if (e) e.preventDefault();
    if (timeLeft === 0) {
      startTimer();
      resendOtp(2);
    }
  };

  const shouldEnableSubmit = () => {
    let enabled = true;

    if (otp === '') {
      enabled = false;
    }
    if (
      otpOrEmailResponse &&
      !otpOrEmailResponse.has_name &&
      fieldsData.name.value.trim() === '' &&
      isTTU &&
      ttuUserName === ''
    ) {
      enabled = false;
    }
    if (
      otpOrEmailResponse &&
      !otpOrEmailResponse.has_email &&
      fieldsData.email.value.trim() === ''
    ) {
      enabled = false;
    }
    if (
      country?.code === 'IN' &&
      otpOrEmailResponse &&
      !otpOrEmailResponse.has_state &&
      fieldsData.state.value.trim() === ''
    ) {
      enabled = false;
    }
    if (sourceType === 'marketing') enabled = true;
    return enabled;
  };

  const onButtonClick = (e) => {
    e.preventDefault();
    if (shouldEnableSubmit()) submitLogin();
  };
  const btnLabel =
    accountType === Constants.ACCOUNT_TYPE_LOGIN ? 'Verify OTP' : 'Submit';
  let subHeading =
    accountType === Constants.ACCOUNT_TYPE_LOGIN
      ? "We've sent an OTP to your registered mobile number & email"
      : "We've sent an OTP to your mobile number";
  let heading =
    accountType === Constants.ACCOUNT_TYPE_LOGIN
      ? 'Enter OTP'
      : 'Verify your mobile number';
  if (otpOrEmailResponse?.phone) {
    subHeading = `We've sent an OTP to your registered mobile number ${otpOrEmailResponse.phone}`;
  }
  if (sourceType === 'marketing') {
    heading = 'Verify mobile number';
    subHeading = 'Enter the 6 digit OTP sent to your mobile number';
  }

  const getTimeLeftStr = () => {
    if (timeLeft > 0) {
      const str = `0${timeLeft}`;
      return ` in 00:${String(str).slice(-2)}`;
    }
    return '';
  };

  useInterval(
    () => {
      setTimeLeft(timeLeft - 1);
    },
    timeLeft > 0 ? 1000 : null
  );

  const { isOtpError, otpErrorMsg } = getOtpError(error);

  return (
    <>
      {!isTTU && <Header>{heading}</Header>}
      {!isTTU && <StyledP1>{subHeading}</StyledP1>}
      {mobileNumber !== '' ? (
        <EditDiv>
          <DisabledTextField
            startAdornment={
              <InputAdornment>
                <CountryMenu
                  changeCountry={changeCountry}
                  country={country}
                  countries={countries}
                  disabled
                />
              </InputAdornment>
            }
            fullWidth
            value={mobileNumber}
            type="tel"
            disabled
            endAdornment={
              isTTU ? null : (
                <StyledUnderlineLink onClick={editMobileNumber} href="#">
                  <StyledH6>Edit</StyledH6>
                </StyledUnderlineLink>
              )
            }
          />
        </EditDiv>
      ) : null}
      <form onSubmit={onButtonClick}>
        <StyledTextField
          fullWidth
          value={otp}
          type="tel"
          placeholder="One time password"
          onChange={onOtpChange}
          autoFocus
          inputProps={{
            type: 'tel',
            maxLength: 6,
            minLength: 4,
            required: true
          }}
          error={isOtpError}
          helperText={otpErrorMsg}
        />
        {!(sourceType === 'marketing') && (
          <EnterOtherFields
            fieldsData={fieldsData}
            setFieldsData={setFieldsData}
            otpOrEmailResponse={otpOrEmailResponse}
            states={states}
            country={country}
            error={error}
            isTTU={isTTU}
          />
        )}
      </form>
      <ButtonWrapper>
        <Verify
          variant={shouldEnableSubmit() && !loggingIn ? 'filled' : 'disabled'}
          label={loggingIn ? 'Submitting...' : btnLabel}
          onClick={onButtonClick}
        />
        <UnderlineLinkWrapper>
          <UnderlineLink disabled={timeLeft > 0} onClick={resend} href="#">
            <Resend
              color={timeLeft > 0 ? TextColors.DISABLED : TextColors.BLACK}
              fixedWidth={timeLeft > 0}
            >
              {`Resend OTP ${getTimeLeftStr()}`}
            </Resend>
          </UnderlineLink>
          {timeLeft === 0 ? (
            <StyledH6WithMargins color={TextColors.DISABLED}>
              or
            </StyledH6WithMargins>
          ) : null}
          {timeLeft === 0 ? (
            <UnderlineLink onClick={onCall} href="#">
              <StyledH6>Call</StyledH6>
            </UnderlineLink>
          ) : null}
        </UnderlineLinkWrapper>
      </ButtonWrapper>
    </>
  );
};

export default EnterOtpStep;

EnterOtpStep.propTypes = {
  accountType: PropTypes.oneOf([
    Constants.ACCOUNT_TYPE_LOGIN,
    Constants.ACCOUNT_TYPE_SIGNUP
  ]),
  mobileNumber: PropTypes.string.isRequired,
  country: PropTypes.objectOf(PropTypes.any).isRequired,
  countries: PropTypes.arrayOf(PropTypes.any).isRequired,
  states: PropTypes.arrayOf(PropTypes.any).isRequired,
  changeCountry: PropTypes.func.isRequired,
  otp: PropTypes.string.isRequired,
  fieldsData: PropTypes.objectOf(PropTypes.any).isRequired,
  setOtp: PropTypes.func.isRequired,
  resendOtp: PropTypes.func.isRequired,
  editMobileNumber: PropTypes.func.isRequired,
  setFieldsData: PropTypes.func.isRequired,
  submitLogin: PropTypes.func.isRequired,
  loggingIn: PropTypes.bool.isRequired,
  otpOrEmailResponse: PropTypes.objectOf(PropTypes.any),
  error: PropTypes.objectOf(PropTypes.any),
  sourceType: PropTypes.string,
  isTTU: PropTypes.bool,
  ttuUserName: PropTypes.string
};

EnterOtpStep.defaultProps = {
  accountType: 'login',
  otpOrEmailResponse: {},
  error: {},
  sourceType: '',
  isTTU: false,
  ttuUserName: ''
};
